import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { PageMain } from "components/molecules";
import { PaziaBox } from "components/atoms";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { PaziaLoader } from "components/atoms";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { useStaticFiles, useUploadStaticFile, useDeleteStaticFile } from "api/hooks";
import { StaticFile } from "api/staticFilesApi";
import { MdContentCopy, MdDelete, MdCheck, MdUploadFile, MdOpenInNew } from "react-icons/md";

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export const StaticFilesPage = () => {
  const { isLoading, files, invalidate } = useStaticFiles();
  const uploadMutation = useUploadStaticFile();
  const deleteMutation = useDeleteStaticFile();

  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleCopyUrl = useCallback((file: StaticFile) => {
    navigator.clipboard.writeText(file.publicUrl);
    setCopiedId(file.id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const handleDelete = useCallback(
    async (file: StaticFile) => {
      if (window.confirm(`Czy na pewno chcesz usunąć plik "${file.fileName}"?`)) {
        await deleteMutation.mutateAsync(file.id);
      }
    },
    [deleteMutation],
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setIsUploading(true);
      try {
        for (const file of acceptedFiles) {
          await uploadMutation.mutateAsync({ file });
        }
      } finally {
        setIsUploading(false);
      }
    },
    [uploadMutation],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".webp", ".jpg", ".jpeg", ".png", ".gif", ".svg"],
      "application/pdf": [".pdf"],
      "text/html": [".html"],
      "text/css": [".css"],
      "application/javascript": [".js"],
      "application/json": [".json"],
      "application/xml": [".xml"],
      "text/plain": [".txt"],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: true,
  });

  const columns = React.useMemo<TanTableColumn<StaticFile>[]>(
    () => [
      {
        id: "fileName",
        accessorKey: "fileName",
        header: "Nazwa pliku",
        enableColumnFilter: false,
        size: 250,
      },
      {
        id: "contentType",
        accessorKey: "contentType",
        header: "Typ",
        enableColumnFilter: false,
        size: 150,
        cell: (info) => (
          <code className="rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-700">
            {info.getValue<string>()}
          </code>
        ),
      },
      {
        id: "fileSize",
        accessorKey: "fileSize",
        header: "Rozmiar",
        enableColumnFilter: false,
        size: 100,
        cell: (info) => formatFileSize(info.getValue<number>()),
      },
      {
        id: "publicUrl",
        accessorKey: "publicUrl",
        header: "Publiczny URL",
        enableColumnFilter: false,
        size: 300,
        cell: (info) => {
          const file = info.row.original;
          return (
            <div className="flex items-center gap-2">
              <code className="truncate rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-700">
                {info.getValue<string>()}
              </code>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyUrl(file);
                }}
                className="flex-shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700"
                title="Kopiuj URL"
              >
                {copiedId === file.id ? (
                  <MdCheck className="h-4 w-4 text-green-500" />
                ) : (
                  <MdContentCopy className="h-4 w-4" />
                )}
              </button>
              <a
                href={info.getValue<string>()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700"
                title="Otwórz w nowym oknie"
              >
                <MdOpenInNew className="h-4 w-4" />
              </a>
            </div>
          );
        },
      },
      {
        id: "uploadedAt",
        accessorKey: "uploadedAt",
        header: "Data dodania",
        enableColumnFilter: false,
        size: 150,
        cell: (info) => info.getValue<Date>()?.toLocaleDateString("pl-PL"),
      },
      {
        id: "actions",
        header: "Akcje",
        size: 100,
        enableColumnFilter: false,
        cell: (info) => {
          const file = info.row.original;
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(file);
              }}
              className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Usuń"
              disabled={deleteMutation.isPending}
            >
              <MdDelete className="h-5 w-5" />
            </button>
          );
        },
      },
    ],
    [copiedId, handleCopyUrl, handleDelete, deleteMutation.isPending],
  );

  return (
    <PageMain>
      <PageRichHeader header="Pliki statyczne">
        <></>
      </PageRichHeader>
      <PaziaBox>
        <div className="mb-6">
          <div
            {...getRootProps()}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              isDragActive
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-slate-300 hover:border-slate-400 dark:border-slate-600"
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center py-4">
              <MdUploadFile className="mb-2 h-12 w-12 text-slate-400" />
              <p className="text-slate-600 dark:text-slate-300">
                {isUploading
                  ? "Przesyłanie..."
                  : isDragActive
                    ? "Upuść pliki tutaj..."
                    : "Przeciągnij pliki tutaj lub kliknij aby wybrać"}
              </p>
              <p className="mt-1 text-sm text-slate-400">
                Dozwolone: obrazy, PDF, HTML, CSS, JS, JSON, XML, TXT (max 10MB)
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <PaziaLoader position="center" />
        ) : files.length === 0 ? (
          <div className="py-8 text-center text-slate-500">Brak przesłanych plików</div>
        ) : (
          <>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Szukaj po nazwie pliku..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:placeholder-slate-500"
              />
            </div>
            <TanTableClient
              data={files.filter((f) =>
                f.fileName.toLowerCase().includes(searchQuery.toLowerCase()),
              )}
              columns={columns}
              hideBorder
            />
          </>
        )}
      </PaziaBox>
    </PageMain>
  );
};
